import '../intrinsic.d.ts';
import { t } from '@trimble/i18n';
import {
  DropdownSelect,
  PanelLayoutWithHeaderToggle,
  SortDirection,
  SortDropDownOption,
  SortDropDownSelect,
} from '@trimble/ui-components/solidjs';
import { OptionalTierLock } from '@trimble/tiering';
import {
  type Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  JSX,
  onCleanup,
  on,
  onMount,
  Show,
} from 'solid-js';
import { Dynamic } from 'solid-js/web';
import {
  FilterOption,
  PROJECT_DATA_FILTER_OPTIONS,
  ProjectDataFilterMode,
} from '../project-data.types';
import {
  BtnClasses,
  CustomTooltip,
  defineSolidCustomInput,
  IconButton,
} from '@tcweb/connect-ui';
import { debounce } from '@solid-primitives/scheduled';
import { ProjectDataPanelContext, SortByKey } from './ProjectDataPanelContext';
import {
  createProjectDataSelection,
  type ProjectDataSelection,
} from './project-data-panel-selection-factory';
import {
  useProjectDataSelectionModifiers,
  type ProjectDataSelectionModifiers,
} from './useProjectDataSelectionModifiers';
import { TieringBehaviors, TieringFeatures } from '@trimble/types';
import type { ProjectDataRepository } from '@trimble/project-data-repository';
import {
  assertUniqueProjectDataSectionIds,
  orderProjectDataSections,
  type ProjectDataPanelConfig,
  type ProjectDataPanelOperations,
  type ProjectDataPanelSectionData,
  type ProjectDataSectionDefinitionMap,
} from './project-data-section-registry';
import { createSearchPanelVisibility } from './search-panel-visibility';
import {
  ConfiguredProjectDataSection,
  getConfiguredSectionDefinition,
} from './ConfiguredProjectDataSection';

const DEBOUNCE_MS = 200;

interface CommonProjectDataPanelProps {
  panelTitle?: string;
  disableAddDataButton?: boolean;
  addDataTooltip?: string;
  featureTieringMap?: FeatureTieringMap;
  onAddDataClick?(): void;
  initialFilterMode?: ProjectDataFilterMode;
  onFilterModeChange?(mode: ProjectDataFilterMode): void;
  /**
   * Filter mode choices. Omitted uses the default options. An empty list hides
   * the filter dropdown.
   */
  filterOptions?: FilterOption<ProjectDataFilterMode>[];
  /** When false, hides the search control and search input (default true). */
  showSearchControl?: boolean;
  /** When false, hides the sort control (default true). */
  showSortControl?: boolean;
  selectedNodeIds?: readonly string[];
  onSelectionChange?(selectedIds: readonly string[]): void;
  /**
   * Called once on mount with the panel-wide `clearSelection` function, so a
   * host (e.g. an e2e test harness) can clear tree selection imperatively
   * without reaching into panel internals.
   */
  onClearSelectionRef?(clearSelection: () => void): void;
  /**
   * Host-owned selection. When omitted the panel creates its own. Pass the
   * matching {@link selectionModifiers} so mouse-down still syncs modifier state.
   */
  selection?: ProjectDataSelection;
  selectionModifiers?: ProjectDataSelectionModifiers;
}

export type ComposedProjectDataPanelProps = CommonProjectDataPanelProps & {
  children: JSX.Element;
  config?: never;
  repository?: never;
  operations?: never;
  sectionData?: never;
  sectionDefinitions?: never;
};

/**
 * Configured panel: sections are driven by {@link config} and {@link sectionDefinitions}.
 *
 * **Data split:** {@link repository} is only for the models tree (`ProjectDataRepository`).
 * Other sections that declare `requiresData` on their definition receive payloads via
 * {@link sectionData} keyed by section id. See `project-data-panel-ui/README.md`.
 */
export type ConfiguredProjectDataPanelProps = CommonProjectDataPanelProps & {
  children?: never;
  config: ProjectDataPanelConfig;
  /** Models section data source; not used by point clouds, alignments, or ortho images. */
  repository: ProjectDataRepository;
  sectionDefinitions: ProjectDataSectionDefinitionMap;
  operations: ProjectDataPanelOperations;
  /** Loadable host data for sections with `requiresData` (e.g. `pointClouds`, `alignments`). */
  sectionData?: ProjectDataPanelSectionData;
};

export type ProjectDataPanelProps =
  | ComposedProjectDataPanelProps
  | ConfiguredProjectDataPanelProps;

export type FeatureTieringMap = Record<'add', TieringFeatures | undefined>;

type ConfigDerivedPanelSettings = {
  panelTitle?: string;
  initialFilterMode?: ProjectDataFilterMode;
  filterOptionKeys?: readonly ProjectDataFilterMode[];
};

function isConfiguredPanel(
  props: ProjectDataPanelProps
): props is ConfiguredProjectDataPanelProps {
  // eslint-disable-next-line solid/reactivity -- the panel variant is chosen once at mount; hosts remount to switch between composed and configured.
  return props.config !== undefined;
}

interface ProjectDataPanelLayoutProps {
  common: CommonProjectDataPanelProps;
  configDerived?: ConfigDerivedPanelSettings;
  contentComponent: Component;
}

const ProjectDataPanelLayout = (
  layoutProps: ProjectDataPanelLayoutProps
): JSX.Element => {
  // eslint-disable-next-line solid/reactivity -- These variant inputs are fixed for the layout component's lifetime.
  const { common: props, configDerived } = layoutProps;

  const [filterText, setFilterText] = createSignal<string>('');
  const [debouncedFilterText, setDebouncedFilterText] =
    createSignal<string>('');
  const [sortBy, setSortBy] = createSignal<SortByKey>('label');
  const [sortDirection, setSortDirection] = createSignal<SortDirection>('asc');
  const [panelScrollElement, setPanelScrollElement] =
    createSignal<HTMLDivElement>();

  const configuredFilterOptions = createMemo(
    (): FilterOption<ProjectDataFilterMode>[] | undefined => {
      const filterOptionKeys = configDerived?.filterOptionKeys;
      if (!filterOptionKeys) {
        return undefined;
      }
      const configuredModes = new Set(filterOptionKeys);
      return PROJECT_DATA_FILTER_OPTIONS().filter((option) =>
        configuredModes.has(option.key)
      );
    }
  );

  const filterModeOptions = createMemo(
    (): FilterOption<ProjectDataFilterMode>[] => {
      return (
        props.filterOptions ??
        configuredFilterOptions() ??
        PROJECT_DATA_FILTER_OPTIONS()
      );
    }
  );

  const resolveInitialFilterMode = (): ProjectDataFilterMode => {
    const optionKeys = filterModeOptions().map((option) => option.key);
    const requested =
      props.initialFilterMode ?? configDerived?.initialFilterMode ?? 'all';
    return optionKeys.includes(requested)
      ? requested
      : (optionKeys[0] ?? 'all');
  };

  const [filterMode, setFilterMode] = createSignal<ProjectDataFilterMode>(
    resolveInitialFilterMode()
  );

  const setFilterTextDebounced = debounce(setDebouncedFilterText, DEBOUNCE_MS);
  onCleanup(() => setFilterTextDebounced.clear());
  const isSearchControlEnabled = (): boolean =>
    props.showSearchControl !== false;

  const searchPanelVisibility = createSearchPanelVisibility(
    isSearchControlEnabled
  );

  createEffect(() => {
    setFilterTextDebounced(filterText());
  });

  const effectiveDebouncedFilterText = createMemo((): string => {
    if (!searchPanelVisibility.isPanelOpen()) {
      return '';
    }
    return debouncedFilterText();
  });

  function clearAndCloseSearch(): void {
    setFilterTextDebounced.clear();
    setFilterText('');
    setDebouncedFilterText('');
    searchPanelVisibility.closePanel();
  }

  createEffect(
    on(
      () => props.showSearchControl,
      (enabled, previous) => {
        if (enabled === false && previous !== false) {
          clearAndCloseSearch();
        }
      }
    )
  );

  createEffect(
    on(
      filterMode,
      (mode) => {
        props.onFilterModeChange?.(mode);
      },
      { defer: true }
    )
  );

  const sortOptions: SortDropDownOption<SortByKey>[] = [
    {
      sortBy: 'label',
      text: t('mobileMapping.projectData.sortOptions.name'),
    },
  ];
  const handleSortChanged = (
    newSortBy: SortByKey,
    newSortDirection: SortDirection
  ): void => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
  };

  const ownedModifiers = useProjectDataSelectionModifiers();
  const modifiers = (): ProjectDataSelectionModifiers =>
    props.selectionModifiers ?? ownedModifiers;
  const ownedSelection = createProjectDataSelection({
    isAdditive: ownedModifiers.isAdditive,
    isRange: ownedModifiers.isRange,
    onSelectionChange: (selectedIds) => props.onSelectionChange?.(selectedIds),
  });
  createEffect(
    on(
      () => props.selectedNodeIds,
      (selectedIds) => {
        if (selectedIds) ownedSelection.setSelection([...selectedIds]);
      }
    )
  );
  const panelSelection = createMemo(
    (): ProjectDataSelection => props.selection ?? ownedSelection
  );

  const handleBackgroundClick = (e: MouseEvent): void => {
    if (e.target !== e.currentTarget) return;
    panelSelection().clearSelection();
  };

  onMount(() => {
    if (!customElements.get('tc-custom-input')) {
      defineSolidCustomInput(true);
    }
    props.onClearSelectionRef?.(panelSelection().clearSelection);
  });

  const panelTitle = createMemo((): string => {
    return (
      props.panelTitle ??
      configDerived?.panelTitle ??
      t('mobileMapping.projectData.projectData')
    );
  });

  const contextValue = {
    filterText: effectiveDebouncedFilterText,
    sortBy,
    sortDirection,
    filterMode,
    setFilterMode,
    panelScrollElement,
    selection: panelSelection,
  };

  const PanelScrollContent = (): JSX.Element => (
    <ProjectDataPanelContext.Provider value={contextValue}>
      <div
        class="flex min-h-full w-full flex-col"
        data-testid="project-data-panel-background"
        onMouseDown={modifiers().syncModifier}
        onClick={handleBackgroundClick}
      >
        <Dynamic component={layoutProps.contentComponent} />
      </div>
    </ProjectDataPanelContext.Provider>
  );

  return (
    <div
      class="w-full h-full bg-base-page text-base-content"
      data-testid="project-data-panel"
    >
      <PanelLayoutWithHeaderToggle
        panelTitle={panelTitle()}
        titleSectionRight={
          <Show when={filterModeOptions().length > 0}>
            <div data-testid="filter-mode-dropdown">
              <DropdownSelect
                options={filterModeOptions()}
                value={filterMode()}
                onValueChange={(value) => {
                  const mode = filterModeOptions().find(
                    (option) => option.key === value
                  )?.key;
                  if (mode) setFilterMode(mode);
                }}
                position="left"
                showOnScroll={true}
              />
            </div>
          </Show>
        }
        rightActions={
          <>
            <Show when={props.onAddDataClick}>
              {(onAddDataClickAccessor) => (
                <OptionalTierLock
                  preserveWidth={true}
                  options={
                    props.featureTieringMap?.add
                      ? {
                          disabledBehavior: TieringBehaviors.Restricted,
                          showBadge: true,
                          featureName: props.featureTieringMap.add!,
                        }
                      : undefined
                  }
                >
                  <CustomTooltip
                    text={props.addDataTooltip}
                    position="right"
                    disableToolTip={!props.addDataTooltip}
                  >
                    <IconButton
                      disabled={props.disableAddDataButton}
                      color={BtnClasses.TERTIARY}
                      icon="tc-icon-content-add"
                      size="icon-medium"
                      clicked={onAddDataClickAccessor()}
                    />
                  </CustomTooltip>
                </OptionalTierLock>
              )}
            </Show>
            <Show when={props.showSortControl !== false}>
              <SortDropDownSelect
                options={sortOptions}
                sortBy={sortBy()}
                sortDirection={sortDirection()}
                onSortChanged={handleSortChanged}
              />
            </Show>
            <Show when={props.showSearchControl !== false}>
              <IconButton
                color={BtnClasses.TERTIARY}
                icon="tc-icon-search"
                size="icon-medium"
                clicked={() => searchPanelVisibility.openPanel()}
              />
            </Show>
          </>
        }
        toggleContent={
          <Show when={props.showSearchControl !== false}>
            <div class="flex items-center justify-between">
              <i class="icon-font tc-icon-search" />
              <tc-custom-input
                name="project-data-search"
                label=""
                class="w-full px-[0.5rem]"
                placeholder={t('general.search')}
                prop:onInputRef={(ref: HTMLInputElement | undefined) => {
                  ref?.focus();
                }}
                prop:onInput={(
                  e: InputEvent & {
                    currentTarget: HTMLInputElement;
                    target: Element;
                  }
                ) => setFilterText(e.currentTarget.value)}
              />
              <IconButton
                color={BtnClasses.TERTIARY}
                icon="tc-icon-content-close"
                size="icon-medium"
                clicked={clearAndCloseSearch}
              />
            </div>
          </Show>
        }
        showToggleContent={searchPanelVisibility.isPanelOpen()}
        contentRef={setPanelScrollElement}
        content={<PanelScrollContent />}
      />
    </div>
  );
};

export const ComposedProjectDataPanel = (
  props: ComposedProjectDataPanelProps
): JSX.Element => {
  const Content = (): JSX.Element => {
    return props.children;
  };

  return <ProjectDataPanelLayout common={props} contentComponent={Content} />;
};

export const ConfiguredProjectDataPanel = (
  props: ConfiguredProjectDataPanelProps
): JSX.Element => {
  // eslint-disable-next-line solid/reactivity -- The configured/composed variant is immutable for a component instance.
  if (props.children !== undefined) {
    throw new Error(
      'ProjectDataPanel accepts either config or children, but not both.'
    );
  }

  const orderedConfiguredSections = createMemo(() => {
    assertUniqueProjectDataSectionIds(props.config.sections);
    return orderProjectDataSections(props.config.sections);
  });

  const repositoryAccessor = (): ProjectDataRepository => props.repository;
  const operationsAccessor = (): ProjectDataPanelOperations => props.operations;
  const sectionDataAccessor = (): ProjectDataPanelSectionData | undefined =>
    props.sectionData;
  const sectionDefinitionsAccessor = (): ProjectDataSectionDefinitionMap =>
    props.sectionDefinitions;
  const Content = (): JSX.Element => (
    <For each={orderedConfiguredSections()}>
      {(section) => (
        <ConfiguredProjectDataSection
          section={section}
          definition={getConfiguredSectionDefinition(
            section,
            sectionDefinitionsAccessor()
          )}
          repository={repositoryAccessor}
          operations={operationsAccessor}
          sectionData={sectionDataAccessor}
        />
      )}
    </For>
  );

  return (
    <ProjectDataPanelLayout
      common={props}
      configDerived={{
        panelTitle: props.config.panelTitle,
        initialFilterMode: props.config.initialFilterMode,
        filterOptionKeys: props.config.filterOptions,
      }}
      contentComponent={Content}
    />
  );
};

export const ProjectDataPanel = (props: ProjectDataPanelProps): JSX.Element => {
  if (isConfiguredPanel(props)) {
    return <ConfiguredProjectDataPanel {...props} />;
  }
  return <ComposedProjectDataPanel {...props} />;
};
